import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { CACHE_TIME } from '@hygge/shared';
import type {
  CreateSprintPayload,
  UpdateSprintPayload,
  StartSprintPayload,
  CompleteSprintPayload,
} from '@hygge/shared';
import {
  getSprints,
  getActiveSprint,
  getSprint,
  getBacklog,
  createSprint,
  updateSprint,
  deleteSprint,
  startSprint,
  completeSprint,
  addTasksToSprint,
  removeTasksFromSprint,
  reorderSprints,
  reorderSprintIssues,
} from '@/lib/sprints';
import { taskKeys } from './use-tasks';

export const sprintKeys = {
  all: ['sprints'] as const,
  lists: () => [...sprintKeys.all, 'list'] as const,
  list: (projectId: string, status?: string) => [...sprintKeys.lists(), projectId, status] as const,
  active: (projectId: string) => [...sprintKeys.all, 'active', projectId] as const,
  details: () => [...sprintKeys.all, 'detail'] as const,
  detail: (id: string) => [...sprintKeys.details(), id] as const,
};

export const backlogKeys = {
  all: ['backlog'] as const,
  list: (projectId: string) => [...backlogKeys.all, projectId] as const,
};

// ─── Queries ──────────────────────────────────────────────────────────

export function useSprints(projectId: string | undefined, status?: string) {
  return useQuery({
    queryKey: sprintKeys.list(projectId ?? '', status),
    queryFn: () => getSprints(projectId!, status),
    enabled: !!projectId,
    staleTime: CACHE_TIME.SHORT,
  });
}

export function useActiveSprint(projectId: string | undefined) {
  return useQuery({
    queryKey: sprintKeys.active(projectId ?? ''),
    queryFn: () => getActiveSprint(projectId!),
    enabled: !!projectId,
    staleTime: CACHE_TIME.SHORT,
  });
}

/**
 * The running sprint on each of several boards, in one hook.
 *
 * Keyed identically to {@link useActiveSprint}, so a board the reader has
 * already opened is served from cache and opening one afterwards costs nothing.
 * A different key here would silently double every request the board pages
 * already make.
 *
 * Request ceiling: one per board. Correct at ~10 boards, linear thereafter.
 * Above roughly 40 this wants a `GET /boards/summary` aggregate, which does not
 * exist and is deliberately not a blocker.
 */
export function useActiveSprints(boardIds: string[]) {
  return useQueries({
    queries: boardIds.map((id) => ({
      queryKey: sprintKeys.active(id),
      queryFn: () => getActiveSprint(id),
      staleTime: CACHE_TIME.SHORT,
    })),
  });
}

export function useSprint(id: string | undefined) {
  return useQuery({
    queryKey: sprintKeys.detail(id ?? ''),
    queryFn: () => getSprint(id!),
    enabled: !!id,
    staleTime: CACHE_TIME.SHORT,
  });
}

export function useBacklog(projectId: string | undefined) {
  return useQuery({
    queryKey: backlogKeys.list(projectId ?? ''),
    queryFn: () => getBacklog(projectId!),
    enabled: !!projectId,
    staleTime: CACHE_TIME.SHORT,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────

export function useCreateSprint(projectId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateSprintPayload) =>
      createSprint(projectId!, payload),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: sprintKeys.all, refetchType: 'all' });
      qc.invalidateQueries({ queryKey: backlogKeys.list(projectId ?? '') });
      qc.invalidateQueries({ queryKey: taskKeys.lists() });
      qc.invalidateQueries({ queryKey: ['board-overview', projectId ?? ''] });
    },
  });
}

export function useUpdateSprint() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & UpdateSprintPayload) =>
      updateSprint(id, payload),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: sprintKeys.lists() });
      qc.invalidateQueries({ queryKey: sprintKeys.detail(variables.id) });
      // No projectId in scope here — refresh the mounted board's overview by prefix.
      qc.invalidateQueries({ queryKey: ['board-overview'] });
    },
  });
}

export function useDeleteSprint(projectId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteSprint(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sprintKeys.lists() });
      qc.invalidateQueries({ queryKey: backlogKeys.list(projectId ?? '') });
      qc.invalidateQueries({ queryKey: ['board-overview', projectId ?? ''] });
    },
  });
}

export function useStartSprint(projectId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & StartSprintPayload) =>
      startSprint(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sprintKeys.lists() });
      qc.invalidateQueries({ queryKey: sprintKeys.active(projectId ?? '') });
      qc.invalidateQueries({ queryKey: ['board-overview', projectId ?? ''] });
    },
  });
}

export function useCompleteSprint(projectId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & CompleteSprintPayload) =>
      completeSprint(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sprintKeys.lists() });
      qc.invalidateQueries({ queryKey: sprintKeys.active(projectId ?? '') });
      qc.invalidateQueries({ queryKey: backlogKeys.list(projectId ?? '') });
      qc.invalidateQueries({ queryKey: taskKeys.lists() });
      qc.invalidateQueries({ queryKey: ['board-overview', projectId ?? ''] });
    },
  });
}

export function useAddTasksToSprint(projectId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ sprintId, taskIds }: { sprintId: string; taskIds: string[] }) =>
      addTasksToSprint(sprintId, { taskIds }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: sprintKeys.detail(variables.sprintId) });
      qc.invalidateQueries({ queryKey: sprintKeys.lists() });
      qc.invalidateQueries({ queryKey: backlogKeys.list(projectId ?? '') });
      qc.invalidateQueries({ queryKey: taskKeys.lists() });
      qc.invalidateQueries({ queryKey: ['board-overview', projectId ?? ''] });
    },
  });
}

export function useRemoveTasksFromSprint(projectId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ sprintId, taskIds }: { sprintId: string; taskIds: string[] }) =>
      removeTasksFromSprint(sprintId, { taskIds }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: sprintKeys.detail(variables.sprintId) });
      qc.invalidateQueries({ queryKey: sprintKeys.lists() });
      qc.invalidateQueries({ queryKey: backlogKeys.list(projectId ?? '') });
      qc.invalidateQueries({ queryKey: taskKeys.lists() });
      qc.invalidateQueries({ queryKey: ['board-overview', projectId ?? ''] });
    },
  });
}

export function useReorderSprints(projectId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (sprintIds: string[]) => reorderSprints(projectId!, sprintIds),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: sprintKeys.lists() });
      qc.invalidateQueries({ queryKey: ['board-overview', projectId ?? ''] });
    },
  });
}

export function useReorderSprintIssues(projectId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ sprintId, taskIds }: { sprintId: string; taskIds: string[] }) =>
      reorderSprintIssues(sprintId, taskIds),
    onSettled: (_data, _err, variables) => {
      qc.invalidateQueries({ queryKey: sprintKeys.detail(variables.sprintId) });
      qc.invalidateQueries({ queryKey: sprintKeys.lists() });
      qc.invalidateQueries({ queryKey: ['board-overview', projectId ?? ''] });
    },
  });
}
